import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomizationStore } from '../store/customizationStore';
import { useLocalizationStore } from '../store/localizationStore';
import { formatCurrency } from '../utils/units';
import { calculateWasteReduction } from '../utils/wasteCalculator';
import type { WasteReduction } from '../types';
import { 
  Sparkles,
  ArrowRight,
  Ruler,
  DollarSign,
  ChevronDown,
  Calculator,
  Lightbulb,
  Leaf,
  TrendingUp,
  ChevronRight,
  Recycle,
  Check,
  X,
  Plus
} from 'lucide-react';

interface WasteSuggestionsProps {
  wasteReduction: WasteReduction;
  onProductSelect: (productType: string) => void;
}

const dropdownVariants = {
  hidden: { 
    height: 0,
    opacity: 0,
    transition: {
      height: { duration: 0.3 },
      opacity: { duration: 0.2 }
    }
  },
  visible: { 
    height: 'auto',
    opacity: 1,
    transition: {
      height: { duration: 0.3 },
      opacity: { duration: 0.2, delay: 0.1 }
    }
  }
};

export const WasteSuggestions: React.FC<WasteSuggestionsProps> = ({
  wasteReduction: initialWasteReduction,
  onProductSelect
}) => {
  const { currency } = useLocalizationStore();
  const { customizationOptions, additionalProducts, addAdditionalProduct, removeAdditionalProduct } = useCustomizationStore();
  const [isExpanded, setIsExpanded] = useState(false);
  const [wasteReduction, setWasteReduction] = useState(initialWasteReduction);

  // Recalculate waste reduction when additional products change
  useEffect(() => {
    if (customizationOptions) {
      const newWasteReduction = calculateWasteReduction(customizationOptions, additionalProducts);
      setWasteReduction(newWasteReduction);
    }
  }, [customizationOptions, additionalProducts]);

  const totalValue = wasteReduction.suggestedProducts.reduce(
    (sum, product) => sum + product.estimatedValue,
    0
  );

  const handleAddProduct = (product: typeof wasteReduction.suggestedProducts[0]) => {
    addAdditionalProduct(product);
  };

  const handleRemoveProduct = (productType: string) => {
    removeAdditionalProduct(productType);
  };

  return (
    <div className="mt-8 border-t border-gold/10 pt-8">
      {/* Dropdown Header */}
      <motion.div
        onClick={() => setIsExpanded(!isExpanded)}
        className="bg-gradient-to-r from-gold/5 to-gold/10 rounded-xl p-6 cursor-pointer hover:from-gold/10 hover:to-gold/15 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gold/10 rounded-xl">
              <Recycle className="w-6 h-6 text-gold" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold text-gold-dark">Material Optimization</h3>
                <span className="bg-gold/20 text-gold px-3 py-1 rounded-full text-sm">
                  {wasteReduction.efficiency.toFixed(1)}% Utilized
                </span>
              </div>
              <p className="text-sm text-black/70 mt-1">
                {wasteReduction.remainingArea.toFixed(2)}m² remaining material available
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {wasteReduction.suggestedProducts.length > 0 && (
              <div className="text-right mr-4">
                <p className="text-sm text-black/70">Potential Value</p>
                <p className="text-lg font-semibold text-gold">
                  {formatCurrency(totalValue, currency)}
                </p>
              </div>
            )}
            <ChevronDown 
              className={`w-6 h-6 text-gold transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          </div>
        </div>
      </motion.div>

      {/* Dropdown Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="mt-4"
          >
            <div className="bg-white rounded-xl shadow-lg border border-gold/10 overflow-hidden">
              {/* Statistics Grid */}
              <div className="grid grid-cols-3 gap-px bg-gold/10">
                <div className="bg-white p-4">
                  <span className="text-sm text-black/70">Total Area</span>
                  <p className="text-xl font-semibold text-gold-dark">
                    {wasteReduction.originalArea.toFixed(2)} m²
                  </p>
                </div>
                <div className="bg-white p-4">
                  <span className="text-sm text-black/70">Used Area</span>
                  <p className="text-xl font-semibold text-gold-dark">
                    {wasteReduction.usedArea.toFixed(2)} m²
                  </p>
                </div>
                <div className="bg-white p-4">
                  <span className="text-sm text-black/70">Remaining</span>
                  <p className="text-xl font-semibold text-gold-dark">
                    {wasteReduction.remainingArea.toFixed(2)} m²
                  </p>
                </div>
              </div>

              {/* Selected Additional Products */}
              {additionalProducts.length > 0 && (
                <div className="p-6 border-b border-gold/10">
                  <div className="flex items-center gap-2 mb-4">
                    <Check className="w-5 h-5 text-green-500" />
                    <h4 className="font-medium text-gold-dark">Selected Additional Products</h4>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    {additionalProducts.map((product) => (
                      <div
                        key={product.type}
                        className="bg-green-50 rounded-xl p-4 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          <div>
                            <h5 className="font-medium text-gold-dark">{product.name}</h5>
                            <p className="text-sm text-black/70">
                              {product.dimensions.width}x{product.dimensions.length}mm
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveProduct(product.type);
                          }}
                          className="p-2 hover:bg-red-100 rounded-full text-red-500 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggested Products */}
              <div className="p-6">
                {wasteReduction.suggestedProducts.length > 0 ? (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <Lightbulb className="w-5 h-5 text-gold" />
                      <h4 className="font-medium text-gold-dark">
                        Suggested Products from Remaining Material
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      {wasteReduction.suggestedProducts
                        .filter(product => !additionalProducts.some(p => p.type === product.type))
                        .map((product) => (
                          <motion.div
                            key={product.type}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="bg-cream rounded-xl p-4 cursor-pointer hover:bg-gold/5 transition-colors"
                          >
                            <div className="flex items-center gap-4">
                              <img
                                src={product.image}
                                alt={product.name}
                                className="w-20 h-20 object-cover rounded-lg"
                              />
                              <div className="flex-1">
                                <h5 className="font-medium text-gold-dark mb-1">{product.name}</h5>
                                <p className="text-sm text-black/70 mb-2">{product.description}</p>
                                <div className="flex items-center gap-4 text-sm">
                                  <span className="flex items-center gap-1">
                                    <Ruler className="w-4 h-4 text-gold" />
                                    {product.dimensions.width}x{product.dimensions.length}mm
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <DollarSign className="w-4 h-4 text-gold" />
                                    Value: {formatCurrency(product.estimatedValue, currency)}
                                  </span>
                                </div>
                              </div>
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddProduct(product);
                                }}
                                className="px-4 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark transition-colors flex items-center gap-2"
                              >
                                Add
                                <Plus className="w-4 h-4" />
                              </motion.button>
                            </div>
                          </motion.div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-black/70">
                    <Sparkles className="w-6 h-6 mx-auto mb-2 text-gold" />
                    <p>No additional products can be made from the remaining material.</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};